import useAccountQuery from 'api/queries/useAccountQuery';
import EBTCardSummary from 'features/shop/cart/EBTPaymentFlow/shared/EBTCardSummary';
import EBTPaymentForm from 'features/shop/cart/EBTPaymentFlow/shared/EBTPaymentForm';
import {
    CreditCardCharge,
    SnapEligibleTotal,
    StepCard,
    StepHeading,
} from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import useEBTPaymentCapture from 'features/shop/cart/EBTPaymentFlow/shared/useEBTPaymentCapture';
import { getSuggestedPaymentAmount } from 'features/shop/cart/EBTPaymentFlow/shared/utils';
import useEBTBalanceCheck from 'shared/ebt/useEBTBalanceCheck';
import { trackEvent } from 'utils/analyticsUtils';

/**
 * CreateEBTPayment component handles the EBT payment creation step in the payment flow.
 *
 * This component allows users to create a SNAP EBT payment by entering their PIN and
 * specifying the payment amount. It includes balance checking functionality and displays
 * the EBT card summary along with SNAP-eligible totals.
 *
 * @param {Object} props - The component props
 * @param {Object} props.cartData - Cart data containing SNAP-eligible totals and other cart information
 * @param {Object} props.ebtCard - EBT card information including last4 digits and payment method reference
 * @param {Object} props.flowData - Flow state data that gets updated with API responses
 * @param {Function} props.handleNext - Callback function to proceed to the next step in the flow
 * @param {Function} props.setDisableClose - Function to control whether the modal/flow can be closed
 * @param {Function} props.setFlowData - Function to update the flow data state
 * @param {Function} props.setIsStepLoading - Function to control the loading state of the current step
 *
 * @returns {JSX.Element} The rendered EBT payment creation form
 */
function CreateEBTPayment({
    cartData,
    currentStepData,
    ebtCard,
    flowData,
    handleNext,
    setDisableClose,
    setFlowData,
    setIsStepLoading,
}) {
    const { data: account = {} } = useAccountQuery();
    const { balance } = useEBTBalanceCheck(ebtCard?.last4);
    const { enableBalanceCheck = true } = currentStepData || {};

    const { captureEBTPayment, clearEBTPaymentCaptureError, ebtPaymentCaptureError, isCapturingEBTPayment } =
        useEBTPaymentCapture();

    const handleSubmit = async (formData) => {
        setIsStepLoading(true);
        setDisableClose(true);
        clearEBTPaymentCaptureError();

        try {
            const response = await captureEBTPayment({
                cartData,
                ebtAmount: formData.amount,
                pinElement: formData.pinElement,
            });

            trackEvent('SNAP payment add', {
                SNAP_eligible_amount: cartData?.snapEligibleTotal || 0,
                SNAP_ineligible_amount: cartData?.snapIneligibleTotal || 0,
                SNAP_payment_amount: Number(formData.amount),
            });

            setFlowData({ ...flowData, apiResponse: response });
            handleNext('CreateEBTPaymentSuccess');
        } finally {
            setIsStepLoading(false);
            setDisableClose(false);
        }
    };

    const suggestedAmount = getSuggestedPaymentAmount({
        snapBalance: balance?.snap,
        snapEligibleTotal: cartData?.snapEligibleTotal,
    });

    return (
        <div className="space-y-15">
            <StepHeading>Pay with SNAP EBT</StepHeading>
            <SnapEligibleTotal total={cartData?.snapEligibleTotal} />
            <CreditCardCharge
                orderChargeDate={account?.nextOrder?.orderChargeDate?.date}
                marketCloseDate={account?.marketCloseDate?.date}
                className="text-body-sm text-grey"
            />
            <StepCard className="space-y-15">
                <EBTCardSummary
                    last4={ebtCard?.last4}
                    className="mb-25"
                    onCheckBalanceClick={() => handleNext('CheckBalance')}
                    enableBalanceCheck={enableBalanceCheck}
                />
                <EBTPaymentForm
                    amountDefault={suggestedAmount}
                    amountSnapBalance={balance?.snap}
                    amountSnapEligibleTotal={cartData?.snapEligibleTotal}
                    error={ebtPaymentCaptureError}
                    isPaymentProcessing={isCapturingEBTPayment}
                    onSubmit={handleSubmit}
                    submitButtonText="Confirm Payment"
                />
            </StepCard>
        </div>
    );
}

export default CreateEBTPayment;
